import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { get } from 'lodash';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import { ADD_TO_CART } from 'mocks/mutations';
import { logError } from 'commons/logger';
import getZipcode from 'commons/getZipcode';
import useToggleCart from 'global-state/isCartOpen/useToggleCart';
import { GET_CART } from '../../mocks/queries';
import { PDP_EXTRA_CUSHION } from '../../commons/constants';
import { LUX_USER_DATA, sendLuxUserData } from '../../commons/lux';
import {
  getItemFromStorage,
  removeItemInStorage,
} from '../../commons/localStorage';

export const getSimpleSelectedOptionsArray = selectedOptions =>
  Object.keys(selectedOptions).map(option_id => ({
    option_id: parseInt(option_id, 10),
    option_value_id: parseInt(selectedOptions[option_id], 10),
  }));

const useCart = () => {
  const [addingToCart, setAddingToCart] = useState(false);
  const { loading: cartLoading, data: cartData, refetch } = useSuspenseQuery(
    GET_CART,
    {
      variables: {
        zipCode: getZipcode(),
      },
    }
  );
  const getCart = async () =>
    refetch({
      zipCode: getZipcode(),
    });

  const [addToCartMutation] = useMutation(ADD_TO_CART);
  const toggleCart = useToggleCart();

  const addToCart = async ({
    productId,
    selectedOptions,
    decideLater,
    selectedProtectionPlanId,
    quantity = 1,
    preventHideCart = false,
  }) => {
    setAddingToCart(true);

    let cart;
    let cartToken;
    let errors;
    try {
      let productOptions = getSimpleSelectedOptionsArray(selectedOptions);

      // options customization
      const extraCushion = getItemFromStorage(
        `${PDP_EXTRA_CUSHION}:${productId}`
      );
      if (extraCushion) {
        productOptions = productOptions.filter(o => o.option_id !== 133);
        productOptions.push({
          option_id: 133,
          option_value_id: extraCushion,
        });
      }

      const res = await addToCartMutation({
        variables: {
          id: productId,
          pickLater: !!decideLater,
          quantity,
          productOptions,
          selectedProtectionPlanId,
          zipCode: getZipcode(),
        },
      });

      errors = get(res, 'data.addToCart.errors', null);

      // remove customizations
      if (!errors) {
        removeItemInStorage(`${PDP_EXTRA_CUSHION}:${productId}`);
      }

      const results = await getCart();
      cart = get(results, 'data.getCart.cart', []);
      cartToken = get(results, 'data.getCart.cart_token', '');
      if (!errors && cart.length && !preventHideCart) {
        await toggleCart();
      }
      sendLuxUserData(LUX_USER_DATA.ADD_TO_CART, true);
    } catch (err) {
      logError(err, 'Add to cart failed');
    }
    setAddingToCart(false);

    return { errors, cart, cartToken };
  };

  return {
    loading: cartLoading,
    data: { addingToCart, cart: cartData?.getCart?.cart },
    actions: { addToCart, getCart },
  };
};

export default useCart;
